/**
 * routes/Funnel — the decision-funnel terminal (IMP.6). One page that answers the
 * owner's standing question — *why is the desk (not) trading right now?* — straight from
 * the server-computed TA.1b analytics, no raw SQL required:
 *
 *  - the funnel itself: candidates screened → full committee ran → ENTER (Arbiter
 *    cleared) → executed (filled trade), each stage with its count and drop-off;
 *  - the final-verdict split (ENTER / WAIT / REJECT) as counts + share of candidates;
 *  - a NO-TRADE drill-down: the gates that vetoed entry, each expandable to the actual
 *    failure reason the desk logged — the concrete "why not";
 *  - committee conviction (Signal & Arbiter confidence histograms) and how often each
 *    role's directional call agreed with the Arbiter's.
 *
 * Everything is read from GET /api/analytics/decisions (+ /api/analytics/summary for the
 * executed tail); the page renders nothing it cannot source. Read-only.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type {
  AnalyticsSummaryResponse,
  ConfidenceBucket,
  DecisionsAnalyticsResponse,
} from '../api/types';
import { usePoll } from '../api/usePoll';
import { useShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { StatTile } from '../components/StatTile';
import { Funnel } from '../components/charts/Funnel';
import type { FunnelStage } from '../lib/analytics';
import { ago, pctPlain } from '../lib/format';

const ROLE_LABEL: Record<string, string> = {
  signal: 'Signal', bear: 'Bear-case', risk: 'Risk', news: 'News', historian: 'Historian', arbiter: 'Arbiter',
};

/** Turn a gate id (`news-window`) into a readable label (`News window`). */
function gateLabel(gate: string): string {
  const s = gate.replace(/[-_]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function FunnelView(): JSX.Element {
  const live = useShell();
  const decisions = usePoll<DecisionsAnalyticsResponse>((s) => api.analyticsDecisions(s), { intervalMs: 20_000 });
  const summary = usePoll<AnalyticsSummaryResponse>((s) => api.analyticsSummary(s), { intervalMs: 30_000 });
  const [openReason, setOpenReason] = useState<string | null>(null);

  const data = decisions.data;
  const funnel = data?.funnel;
  const executed = summary.data?.totalTrades ?? null;
  const source = live.overview?.source;

  // The funnel is a single cohort — the decisions in this window — so it only ever
  // narrows: candidates → committee → ENTER. "Executed" (all-time closed trades) is a
  // different population/window, so it is shown as downstream context, never as a bar
  // (folding it in would render a misleading >100% stage against this window).
  const stages: FunnelStage[] = useMemo(() => {
    if (!funnel) return [];
    return [
      { key: 'candidates', label: 'Candidates screened', count: funnel.candidates },
      { key: 'committee', label: 'Full committee ran', count: funnel.committeeRun },
      { key: 'enter', label: 'ENTER — Arbiter cleared', count: funnel.enter },
    ];
  }, [funnel]);

  const cand = funnel?.candidates ?? 0;
  const reasons = data?.noTradeReasons ?? [];

  if (decisions.loading && !data) {
    return (
      <>
        <PageHead updatedAt={decisions.updatedAt} source={source} />
        <div className="state"><span className="spinner" aria-hidden /></div>
      </>
    );
  }

  if (!funnel || cand === 0) {
    return (
      <>
        <PageHead updatedAt={decisions.updatedAt} source={source} />
        <div className="banner-note section" role="status">
          <strong>No decisions yet.</strong> The committee has not screened any candidates in this window — the funnel
          populates as soon as the desk starts evaluating setups.
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead updatedAt={decisions.updatedAt} source={source} />

      {/* ── the funnel ─────────────────────────────────────────────────────── */}
      <Card title="Decision funnel" hint={`${cand} decision${cand === 1 ? '' : 's'} in window`}>
        <Funnel stages={stages} />
        {executed != null && (
          <p className="funnel-foot muted">
            Downstream execution: {executed} closed trade{executed === 1 ? '' : 's'} all-time (a different window from this
            decision cohort — see Trades).
          </p>
        )}
      </Card>

      {/* ── verdict split ──────────────────────────────────────────────────── */}
      <div className="stat-grid stat-grid--4 section">
        <StatTile
          label="ENTER"
          value={funnel.enter}
          sub={`${pctPlain(cand ? funnel.enter / cand : 0, 0)} of candidates`}
          tone={funnel.enter > 0 ? 'profit' : 'neutral'}
        />
        <StatTile
          label="WAIT"
          value={funnel.wait}
          sub={`${pctPlain(cand ? funnel.wait / cand : 0, 0)} of candidates`}
          tone={funnel.wait > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="REJECT"
          value={funnel.reject}
          sub={`${pctPlain(cand ? funnel.reject / cand : 0, 0)} of candidates`}
          tone={funnel.reject > 0 ? 'loss' : 'neutral'}
        />
        <StatTile
          label="No-trade total"
          value={funnel.noTrade}
          sub="WAIT + REJECT"
          tone={funnel.noTrade > 0 ? 'neutral' : 'profit'}
        />
      </div>

      {/* ── why the desk did not trade (drill-down) ────────────────────────── */}
      <Card title="Why the desk did not trade" hint="gate vetoes · click to expand">
        {reasons.length > 0 ? (
          <>
            <ul className="reasons" role="list">
              {reasons.map((r) => {
                const top = reasons[0]!.count || 1;
                const open = openReason === r.gate;
                const hasDetail = r.sampleReason != null && r.sampleReason.length > 0;
                return (
                  <li className="reasons__item" key={r.gate}>
                    <button
                      type="button"
                      className="reasons__row"
                      aria-expanded={open}
                      disabled={!hasDetail}
                      onClick={() => setOpenReason(open ? null : r.gate)}
                    >
                      <span className="reasons__caret" aria-hidden>{hasDetail ? (open ? '▾' : '▸') : '·'}</span>
                      <span className="reasons__label" title={r.gate}>{gateLabel(r.gate)}</span>
                      <span className="reasons__track">
                        <span className="reasons__fill" style={{ width: `${(r.count / top) * 100}%` }} />
                      </span>
                      <span className="reasons__count num">{r.count}</span>
                    </button>
                    {open && hasDetail && (
                      <p className="reasons__detail">{r.sampleReason}</p>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="funnel-foot muted">
              Gate vetoes blocked entry on the decisions above (one decision can trip more than one gate). The remaining
              no-trades — {funnel.noTrade} in total — are Arbiter WAIT/REJECT calls where every gate passed.
            </p>
          </>
        ) : (
          <div className="state">
            <span className="muted">
              {funnel.noTrade > 0
                ? `No gate vetoes in range — all ${funnel.noTrade} no-trades were Arbiter WAIT/REJECT calls.`
                : 'No no-trade decisions in range.'}
            </span>
          </div>
        )}
      </Card>

      {/* ── committee conviction ───────────────────────────────────────────── */}
      <div className="grid-2--even section">
        <Card title="Signal conviction" hint="confidence across all decisions">
          <ConfidenceStrip buckets={data.signalConfidence} />
        </Card>
        <Card title="Arbiter conviction" hint="confidence across all decisions">
          <ConfidenceStrip buckets={data.arbiterConfidence} />
        </Card>
      </div>

      {/* ── agreement with the Arbiter ─────────────────────────────────────── */}
      <Card title="Role agreement with Arbiter" hint="directional call match rate">
        {data.agreementRates.some((a) => a.n > 0) ? (
          <div className="dbar">
            {data.agreementRates.map((a) => (
              <div className="dbar__row" key={a.role}>
                <span className="dbar__label" title={ROLE_LABEL[a.role] ?? a.role}>{ROLE_LABEL[a.role] ?? a.role}</span>
                <div className="dbar__track">
                  <span
                    className="dbar__fill dbar__fill--seq"
                    style={{ width: `${Math.max(2, a.rate * 100)}%` }}
                    title={`${ROLE_LABEL[a.role] ?? a.role}: ${pctPlain(a.rate, 0)} over ${a.n}`}
                  />
                </div>
                <span className="dbar__val num">
                  {a.n > 0 ? pctPlain(a.rate, 0) : '—'} <span className="muted">· {a.n}</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="state"><span className="muted">Not enough readable votes to compare against the Arbiter yet.</span></div>
        )}
      </Card>
    </>
  );
}

function PageHead({ updatedAt, source }: { updatedAt: number | null; source: string | undefined }): JSX.Element {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-head__title">Decision funnel</h1>
        <span className="page-head__sub">
          {updatedAt ? `updated ${ago(updatedAt)}` : 'loading…'}
          {source === 'demo-seed' && <span className="badge badge--neutral" style={{ marginLeft: 8 }}>demo seed</span>}
        </span>
      </div>
    </div>
  );
}

/** A compact 10-bucket confidence histogram: vertical bars, 0 → 100% conviction, left to right. */
function ConfidenceStrip({ buckets }: { buckets: readonly ConfidenceBucket[] }): JSX.Element {
  const total = buckets.reduce((s, b) => s + b.count, 0);
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  if (total === 0) {
    return (
      <div className="state state--empty">
        <span className="empty-tag">NO DATA YET</span>
        <span className="muted">No confidence values recorded.</span>
      </div>
    );
  }
  return (
    <div className="conf">
      <div className="conf__bars" role="img" aria-label="Confidence distribution from 0 to 100 percent">
        {buckets.map((b) => (
          <span
            key={b.label}
            className="conf__bar"
            style={{ height: `${max > 0 ? Math.max(3, (b.count / max) * 100) : 3}%` }}
            title={`${b.label}%: ${b.count}`}
          />
        ))}
      </div>
      <div className="conf__axis muted">
        <span>0%</span><span>{total} vote{total === 1 ? '' : 's'}</span><span>100%</span>
      </div>
    </div>
  );
}
